import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpRequest,
  HttpParams
} from '@angular/common/http';
import { PatientData } from '../components/types/patient-data';
import { PatientService } from './patient.service';

export interface BasicUser {
  email: string;
  firstName: string;
  lastName: string;
}

export interface BasicAgency {
  id: number;
  camId: number;
  name: string;
  slug: string;
  logoUrl: string;
}

export interface Agency {
  id: number;
  camId: number;
  name: string;
  phone: string;
  email: string;
  emailReturnAddress: string;
  slug: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  enabled: boolean;
  deleted: boolean;
  logoUrl: string;
  // Simplified email customization fields
  emailSignature?: string;
  emailWelcomeBlock?: string;
}

export interface IntakeForm {
  id: number;
  name: string;
  program: string;
  eSignable: boolean;
  displayOrder: number;
  consentedFileId?: number; // UploadedFileId if previously consented to (eSign) form.
  consented?: boolean; // If the user is consenting to (eSign) form terms.
  viewed?: boolean; // If the user has viewed the (eSign) form.
  presignedUrl?: string; // Only present for initial upload response
  isJotForm?: boolean; // Calculated field
}

export interface UploadedFile {
  id: number;
  intakeFormId: number;
  name: string;
  program: string;
  mimeType: string;
  stamped: boolean;
  presignedUrl?: string; // Only present for initial upload response
}

export interface FileUploadResponse {
  presignedS3URL: string;
}

export interface SubmittedPacket {
  id: number;
  createdAt: string;
  eSign: boolean;
  program: string;
}

export interface Appointment {
  id: number;
  displayedAppointmentDate: string; // "hh:mmaa 'on' MM/dd/yyyy"
  location: string;
  staffMember: string;
  status: 'Upcoming' | 'Attended' | 'Canceled' | 'No Show';
  appointmentDate: string; // "yyyy-MM-dd"
}

export interface AppointmentRequest {
  requestType: string;
  appointmentDate: string;
  appointmentTime: string;
  location: string;
  staffMember: string;
  appointmentId: number;
  message: string;
  displayedAppointmentDate: string;
}

export interface PatientMessageData {
  messageType: string;
  messageBody: string;
  messageCreationDate: string;
}

export interface ResourceLink {
  linkUrl: string;
  linkText: string;
  displayOrder: number;
}

export interface ConfirmResponse {
  cognitoAccountCreated: boolean;
}

export interface PatientListPage {
  content: PatientListData[];
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  first: boolean;
  sort: string;
  numberOfElements: number;
}
export interface PatientListData {
  name: string;
  email: string;
  id: number;
}

export interface SortedForms {
  program: string;
  forms: IntakeForm[];
  isJotForm?: boolean;
  uploadedJotForms?: UploadedFile[];
}

@Injectable({
  providedIn: 'root'
})
export class RestApiService {
  constructor(private client: HttpClient, private patient: PatientService) {}

  agencySlug(): string {
    return this.patient.currentAgency.slug;
  }

  errorMessage(error: any): string {
    if (typeof error === 'string') return error;
    if (typeof error === 'undefined') return 'undefined error';
    if (error === null) return 'null error';
    if (error.error && error.error.message) return error.error.message;
    if (error.message) return error.message;
    return JSON.stringify(error);
  }

  async confirmAccountInfo(confirmationKey: string): Promise<BasicUser> {
    return this.client
      .get<BasicUser>(
        '/api/public/agency/' +
          this.agencySlug() +
          '/confirm/' +
          confirmationKey
      )
      .toPromise();
  }

  async confirmAccount(confirmationKey: string): Promise<ConfirmResponse> {
    return this.client
      .post<ConfirmResponse>(
        '/api/public/agency/' +
          this.agencySlug() +
          '/confirm/' +
          confirmationKey,
        {}
      )
      .toPromise();
  }

  async resetPassword(email: string): Promise<any> {
    return this.client
      .post('/api/public/agency/' + this.agencySlug() + '/resetPassword', {
        email
      })
      .toPromise();
  }

  async confirmPasswordReset(confirmationKey: string): Promise<any> {
    return this.client
      .post(
        '/api/public/agency/' +
          this.agencySlug() +
          '/resetPassword/' +
          confirmationKey,
        {}
      )
      .toPromise();
  }

  async currentPatientData(): Promise<PatientData> {
    return this.client
      .get('/api/common/agency/' + this.agencySlug() + '/patient')
      .toPromise()
      .then(json => new PatientData(json));
  }

  // *************** Patient Intake Document Calls ****************
  async listIntakeDocuments(): Promise<IntakeForm[]> {
    return this.client
      .get<IntakeForm[]>(
        '/api/patient/agency/' + this.agencySlug() + '/intake/form'
      )
      .toPromise();
  }

  async downloadIntakeForm(formId: number): Promise<Blob> {
    return this.client
      .get(
        '/api/patient/agency/' + this.agencySlug() + '/intake/form/' + formId,
        { responseType: 'blob' }
      )
      .toPromise();
  }

  async downloadFlattenedIntakeForm(formId: number): Promise<Blob> {
    return this.client
      .get(
        '/api/patient/agency/' +
          this.agencySlug() +
          '/intake/form/' +
          formId +
          '/flat',
        { responseType: 'blob' }
      )
      .toPromise();
  }

  async submitESignPacket(
    signerEmail: string,
    signerPassword: string,
    intakeForms: IntakeForm[]
  ): Promise<any> {
    return this.client
      .post(`/api/patient/agency/${this.agencySlug()}/esign/packet`, {
        signerEmail,
        signerPassword,
        consentedIntakeFormIds: intakeForms.map(intakeForm => intakeForm.id)
      })
      .toPromise();
  }

  async listESignPackets(): Promise<SubmittedPacket[]> {
    return this.client
      .get<SubmittedPacket[]>(
        '/api/patient/agency/' + this.agencySlug() + '/esign/packet'
      )
      .toPromise();
  }

  async submitNonESignPacket(uploadedFileIds: number[]): Promise<any> {
    return this.client
      .post(`/api/patient/agency/${this.agencySlug()}/intake/packet`, {
        uploadedFileIds
      })
      .toPromise();
  }

  async listNonESignPackets(): Promise<SubmittedPacket[]> {
    return this.client
      .get<SubmittedPacket[]>(
        '/api/patient/agency/' + this.agencySlug() + '/intake/packet'
      )
      .toPromise();
  }

  async downloadSubmittedPacketFile(packetId: number): Promise<Blob> {
    return this.client
      .get(
        '/api/patient/agency/' +
          this.agencySlug() +
          '/intake/packet/' +
          packetId,
        { responseType: 'blob' }
      )
      .toPromise();
  }

  async listUploadedFiles(): Promise<UploadedFile[]> {
    return this.client
      .get<UploadedFile[]>(
        '/api/patient/agency/' + this.agencySlug() + '/intake/file'
      )
      .toPromise();
  }

  async downloadUploadedFile(fileId: number): Promise<Blob> {
    return this.client
      .get(
        '/api/patient/agency/' + this.agencySlug() + '/intake/file/' + fileId,
        { responseType: 'blob' }
      )
      .toPromise();
  }

  async uploadNonEsignIntakeFile(
    program: string,
    file: File
  ): Promise<UploadedFile> {
    let uploadedFile: UploadedFile;
    // Step 1: write an intake form record to the DB as a non-visible form and get upload URL
    const request = {
      program: program,
      name: file.name,
      contentType: file.type,
      size: file.size
    };
    return this.client
      .post<UploadedFile>(
        '/api/patient/agency/' + this.agencySlug() + '/intake/file',
        request
      )
      .toPromise()
      .then(response => {
        // Step 2: write the file content to S3
        uploadedFile = response;
        const headers = new HttpHeaders({
          'Content-Type': file.type,
          'x-amz-acl': 'bucket-owner-full-control'
        });
        const req = new HttpRequest('PUT', uploadedFile.presignedUrl, file, {
          headers
        });
        // add param reportProgress:true next to headers to see ongoing progress
        return this.client.request(req).toPromise(); // returns 200 with an empty response
      })
      .then(() => uploadedFile);
  }

  async createAppointmentRequest(
    appointmentRequest: AppointmentRequest
  ): Promise<any> {
    return this.client
      .post(
        '/api/patient/agency/' + this.agencySlug() + '/appointment',
        appointmentRequest
      )
      .toPromise();
  }

  async getAppointments(): Promise<Appointment[]> {
    return this.client
      .get<Appointment[]>(
        '/api/patient/agency/' + this.agencySlug() + '/appointments'
      )
      .toPromise();
  }

  async createPatientMessage(patientMessage: PatientMessageData): Promise<any> {
    // Capture the current timestamp so we can display a reasonable time in the patient email
    // being sent by Spring
    patientMessage.messageCreationDate = new Date().toLocaleString();
    return this.client
      .post(
        '/api/patient/agency/' + this.agencySlug() + '/message',
        patientMessage
      )
      .toPromise();
  }

  async getAgencyInfo(agencySlug: string): Promise<Agency> {
    // Returns only enabled agencies
    return this.client
      .get<Agency>('/api/patient/agency/' + agencySlug)
      .toPromise();
  }

  async getResourceLinks(): Promise<ResourceLink[]> {
    return this.client
      .get<ResourceLink[]>(
        '/api/patient/agency/' + this.agencySlug() + '/resources'
      )
      .toPromise();
  }

  async getPatientMetadata(): Promise<any> {
    return this.client.get('/api/outbound/agency/' + this.agencySlug() + '/patient/metadata').toPromise();
  }

  async postTransaction(transactionData: any): Promise<any> {
    try {
      return await this.client.post('/api/outbound/agency/' + this.agencySlug() + '/transactions', transactionData).toPromise();
    } catch (error) {
      console.log('REST API Error:', error);
      console.log('Error status:', error.status);
      console.log('Error body:', error.error);

      // If the backend returned a structured error response, preserve it
      if (error.status && error.error && (error.error.message || error.error.status)) {
        throw {
          status: error.status,
          error: error.error
        };
      }

      // Otherwise, re-throw the original error
      throw error;
    }
  }

  // *************** Admin Calls ****************
  async adminAgencyInfo(agencySlug: string): Promise<Agency> {
    // This one can return enabled or disabled (but not deleted) agencies
    return this.client
      .get<Agency>('/api/admin/agency/' + agencySlug)
      .toPromise();
  }

  async getPatients(
    agencySlug: string,
    page: number,
    itemsPerPage: number,
    search: string
  ): Promise<PatientListPage> {
    const params: HttpParams = new HttpParams({
      fromObject: {
        page: page.toString(),
        size: itemsPerPage.toString(),
        search
      }
    });
    return this.client
      .get<PatientListPage>('/api/admin/agency/' + agencySlug + '/patients', {
        params
      })
      .toPromise();
  }

  async adminSaveResourceLinks(
    agencySlug: string,
    links: ResourceLink[]
  ): Promise<any> {
    return this.client
      .post(
        '/api/admin/agency/' + agencySlug + '/resources',
        links.filter(link => link.linkUrl)
      )
      .toPromise();
  }

  async getAdminResourceLinks(agencySlug: string): Promise<ResourceLink[]> {
    return this.client
      .get<ResourceLink[]>('/api/admin/agency/' + agencySlug + '/resources')
      .toPromise();
  }

  async adminEnabledAgencies(search: string): Promise<Agency[]> {
    const params: HttpParams = new HttpParams().set('search', search);
    return this.client
      .get<Agency[]>('/api/admin/agency', { params })
      .toPromise();
  }

  async adminDisabledAgencies(search: string): Promise<Agency[]> {
    const params: HttpParams = new HttpParams().set('search', search);
    return this.client
      .get<Agency[]>('/api/admin/agency/disabled', { params })
      .toPromise();
  }

  async adminSaveAgencyDetails(agency: Agency): Promise<Agency> {
    if (agency.id && agency.id > 0)
      return this.client
        .post<Agency>('/api/admin/agency/' + agency.id, agency)
        .toPromise();
    else
      return this.client.post<Agency>('/api/admin/agency', agency).toPromise();
  }

  async adminUploadAgencyLogo(agency: BasicAgency, file: File): Promise<any> {
    // Step 1: get upload URL
    return this.client
      .post<FileUploadResponse>('/api/admin/agency/' + agency.slug + '/logo', {
        name: file.name,
        contentType: file.type,
        size: file.size
      })
      .toPromise()
      .then(response => {
        // Step 2: write the file content to S3
        const headers = new HttpHeaders({
          'Content-Type': file.type,
          'x-amz-acl': 'public-read'
        });
        const req = new HttpRequest('PUT', response.presignedS3URL, file, {
          headers
        });
        // add param reportProgress:true next to headers to see ongoing progress
        return this.client.request(req).toPromise(); // returns 200 with an empty response
      });
  }

  async adminListIntakeDocuments(agency: BasicAgency): Promise<IntakeForm[]> {
    return this.client
      .get<IntakeForm[]>('/api/admin/agency/' + agency.slug + '/intake/form')
      .toPromise();
  }

  async adminUploadIntakeDocument(
    agency: BasicAgency,
    program: String,
    file: File,
    eSignable: boolean
  ): Promise<IntakeForm> {
    let intakeForm: IntakeForm;
    // Step 1: write an intake form record to the DB as a non-visible form and get upload URL
    return this.client
      .post<IntakeForm>('/api/admin/agency/' + agency.slug + '/intake/form', {
        name: file.name,
        program: program,
        contentType: file.type,
        size: file.size,
        eSignable
      })
      .toPromise()
      .then(response => {
        // Step 2: write the file content to S3
        intakeForm = response;
        const headers = new HttpHeaders({
          'Content-Type': file.type,
          'x-amz-acl': 'bucket-owner-full-control'
        });
        const req = new HttpRequest('PUT', intakeForm.presignedUrl, file, {
          headers
        });
        // add param reportProgress:true next to headers to see ongoing progress
        return this.client.request(req).toPromise(); // returns 200 with an empty response
      })
      .then(() => {
        // Step 3: now that upload is complete, update the intake form record to be visible
        return this.client
          .post<IntakeForm>(
            '/api/admin/agency/' +
              agency.slug +
              '/intake/form/' +
              intakeForm.id,
            {
              name: intakeForm.name,
              program: program,
              displayOrder: intakeForm.displayOrder,
              eSignable: intakeForm.eSignable,
              deleted: false,
              validate: true // e.g. ensure an e-Sign form has the necessary fields.  On failure, doc will be left as "deleted".
            }
          )
          .toPromise();
      });
  }

  async adminDownloadIntakeForm(
    agency: BasicAgency,
    formId: number
  ): Promise<Blob> {
    return this.client
      .get('/api/admin/agency/' + agency.slug + '/intake/form/' + formId, {
        responseType: 'blob'
      })
      .toPromise();
  }

  async adminDeleteIntakeForm(
    agency: BasicAgency,
    formId: number
  ): Promise<any> {
    return this.client
      .delete('/api/admin/agency/' + agency.slug + '/intake/form/' + formId)
      .toPromise();
  }

  async adminReorderIntakeForms(
    agency: BasicAgency,
    formIdsInOrder: number[]
  ): Promise<any> {
    return this.client
      .post('/api/admin/agency/' + agency.slug + '/intake/form/order', {
        idsInOrder: formIdsInOrder
      })
      .toPromise();
  }

  // *************** Integration Calls ****************
  async recordLogin(): Promise<any> {
    return this.client
      .post('/api/outbound/agency/' + this.agencySlug() + '/login', {})
      .toPromise();
  }
}
